/**
 * usePoolFeed — one read model for a pool's query feed (Large Sets PRD §4.3).
 *
 * Resolves the bound saved query, evaluates it over the candidate items, and
 * returns the phase-appropriate view: the diff a refresh would show while the
 * pool is open, or the Arrival Bay once the feed is severed at lock. Nothing
 * here writes membership; recording a new sighting as 'held' in pool_arrivals
 * is the only side effect, and an arrival is not a seat.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useBoundSavedReport } from '../../hooks/useBoundSavedReport';
import type { Item, ItemPool } from '../../types/prioritization/lifecycle';
import type { SavedReport } from '../../types/prioritization/savedReports';
import { filterGroupsToAst } from '../../lib/pql';
import {
  computeFeedDiff,
  coverageAtLock,
  evaluateFeedMatchIds,
  feedPhase,
  resolveArrivalBay,
  type ArrivalBay,
  type ArrivalRecord,
  type FeedDiff,
  type FeedPhase,
  type LockCoverage,
} from '../../lib/poolFeedCore';
import { fetchArrivals, recordArrivals } from '../../lib/poolFeedService';

export interface PoolFeedView {
  phase: FeedPhase;
  report: SavedReport | undefined;
  /** True while the bound report is not yet in the store (library fetch in flight). */
  loadingRule: boolean;
  matchedIds: string[];
  diff: FeedDiff;
  coverage: LockCoverage;
  arrivals: ArrivalRecord[];
  bay: ArrivalBay;
  reloadArrivals: () => Promise<void>;
}

const EMPTY_DIFF: FeedDiff = { adds: [], removals: [], kept: [] };
const EMPTY_BAY: ArrivalBay = { newArrivals: [], held: [], staleHeld: [], dismissed: [], carried: [], amended: [] };

export function usePoolFeed(pool: ItemPool | undefined, candidateItems: Item[]): PoolFeedView {
  const feedQueryId = pool?.feedQueryId ?? null;
  // The library may not be loaded on the pool page — the hook fetches it once
  // when the bound report is missing.
  const { report, loading: loadingRule } = useBoundSavedReport(feedQueryId);

  const phase: FeedPhase = pool ? feedPhase(pool) : 'unfed';

  const ruleAst = useMemo(() => {
    if (!report) return undefined;
    if (report.queryAst !== undefined) return report.queryAst;
    return filterGroupsToAst(report.filterGroups ?? []);
  }, [report]);

  const matchedIds = useMemo(
    () => (phase === 'unfed' || !report ? [] : evaluateFeedMatchIds(candidateItems, ruleAst)),
    [phase, report, candidateItems, ruleAst],
  );

  const poolPriorityIds = pool?.priorityIds;
  const memberIds = useMemo(() => poolPriorityIds ?? [], [poolPriorityIds]);
  const diff = useMemo(
    () => (phase === 'open' ? computeFeedDiff(memberIds, matchedIds) : EMPTY_DIFF),
    [phase, memberIds, matchedIds],
  );
  const coverage = useMemo(() => coverageAtLock(diff), [diff]);

  // ── Arrival Bay (severed pools) ─────────────────────────────────────────
  const [arrivals, setArrivals] = useState<ArrivalRecord[]>([]);
  const poolId = pool?.id;
  const reloadArrivals = useMemo(
    () => async () => {
      if (!poolId) return;
      const res = await fetchArrivals(poolId);
      if (!res.error) setArrivals(res.data);
    },
    [poolId],
  );
  useEffect(() => {
    setArrivals([]);
    if (phase === 'severed') void reloadArrivals();
  }, [phase, reloadArrivals]);

  const bay = useMemo(
    () => (phase === 'severed' ? resolveArrivalBay({ matchedIds, memberIds, arrivals }) : EMPTY_BAY),
    [phase, matchedIds, memberIds, arrivals],
  );

  // Record new sightings as held — once per item per mount, so a slow
  // round-trip cannot loop.
  const recordedRef = useRef(new Set<string>());
  useEffect(() => {
    if (phase !== 'severed' || !pool) return;
    const fresh = bay.newArrivals.filter((id) => !recordedRef.current.has(id));
    if (fresh.length === 0) return;
    fresh.forEach((id) => recordedRef.current.add(id));
    void recordArrivals(pool, fresh).then((res) => {
      if (!res.error) void reloadArrivals();
    });
  }, [phase, pool, bay.newArrivals, reloadArrivals]);

  return {
    phase,
    report,
    loadingRule,
    matchedIds,
    diff,
    coverage,
    arrivals,
    bay,
    reloadArrivals,
  };
}
